import { addDays, isSameDay as dfnsIsSameDay, format, isValid, parse } from 'date-fns';
import { Timeframe } from '../types';

const ET_TIMEZONE = 'America/New_York';

const createETFormatter = () =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    timeZoneName: 'longOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

export const getCurrentETDateString = (): string => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    timeZoneName: 'longOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(now);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;

  return `${year}-${month}-${day}`;
};

export const getCurrentETDate = (): Date => {
  const etDateString = getCurrentETDateString();
  return new Date(etDateString + 'T00:00:00.000Z');
};

export const toETDate = (date: Date | string): Date => {
  if (typeof date === 'string') {
    return new Date(date + 'T00:00:00.000Z');
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    timeZoneName: 'longOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const etDateString = `${year}-${month}-${day}`;

  return new Date(etDateString + 'T00:00:00.000Z');
};

export const startOfDayET = (date: Date | string): Date => {
  if (date instanceof Date && date.getUTCHours() === 0 && date.getUTCMinutes() === 0) {
    return date;
  }
  return toETDate(date);
};

export const endOfDayET = (date: Date | string): Date => {
  let startOfDay: Date;

  if (date instanceof Date && date.getUTCHours() === 0 && date.getUTCMinutes() === 0) {
    startOfDay = date;
  } else {
    startOfDay = toETDate(date);
  }

  return new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000 - 1);
};

export const addDaysToDate = (date: Date | string, days: number): Date => {
  const baseDate = toETDate(date);
  return addDays(baseDate, days);
};

export const isSameDay = (date1: Date | string, date2: Date | string): boolean => {
  const etDate1 = toETDate(date1);
  const etDate2 = toETDate(date2);
  return dfnsIsSameDay(etDate1, etDate2);
};

export const formatDateToString = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    return '';
  }

  return format(dateObj, 'MM/dd/yyyy');
};

export const formatDateForAPI = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
};

export const getDateRangeForTimeframe = (timeframe: Timeframe): { from: Date; to: Date } => {
  const todayET = getCurrentETDate();
  const todayETString = getCurrentETDateString();

  let result: { from: Date; to: Date };

  switch (timeframe) {
    case 'Last 7 Days':
      const sevenDaysAgo = addDaysToDate(todayET, -7);
      result = {
        from: startOfDayET(sevenDaysAgo),
        to: endOfDayET(todayET),
      };
      break;
    case 'Today':
      result = {
        from: toETDate(todayETString),
        to: endOfDayET(todayETString),
      };
      break;
    case 'Yesterday':
      const yesterday = addDaysToDate(todayETString, -1);
      result = {
        from: startOfDayET(yesterday),
        to: endOfDayET(yesterday),
      };
      break;
    case 'Tomorrow':
      const tomorrow = addDaysToDate(todayETString, 1);
      result = {
        from: startOfDayET(tomorrow),
        to: endOfDayET(tomorrow),
      };
      break;
    case 'Next 7 Days':
      const sevenDaysFromNow = addDaysToDate(todayET, 7);
      result = {
        from: startOfDayET(todayET),
        to: endOfDayET(sevenDaysFromNow),
      };
      break;
    default:
      result = {
        from: startOfDayET(todayET),
        to: endOfDayET(todayET),
      };
  }

  return result;
};

export const getETTimezoneInfo = (): string => {
  const formatter = createETFormatter();
  const parts = formatter.formatToParts(new Date());
  return parts.find((part) => part.type === 'timeZoneName')?.value || '';
};

export const formatDateTimeToAMPM = (dateString: string, timeString: string): string => {
  try {
    const dateTimeString = `${dateString} ${timeString}`;

    const dateObj = parse(dateTimeString, 'yyyy-MM-dd HH:mm', new Date());

    return format(dateObj, 'yyyy-MM-dd h:mm a');
  } catch (error) {
    console.warn('Error formatting date time:', error);
    return `${dateString} ${timeString}`;
  }
};
